import React, {
   useCallback,
   useEffect,
   useRef,
   useState,
   useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";

import hideIcon from "../../assets/svg/material-symbols--left-panel-close.svg";
import {
   closeStaffChatWorkspace,
   expandStaffChatWorkspace,
   getStaffChatWorkspaceState,
   retractStaffChatWorkspace,
   subscribeStaffChatWorkspace,
} from "../../stores/staffChatWorkspaceStore";
import { getCurrentPopup, subscribePopup } from "../Common/popupUIStore";
import { PopupActionsContext } from "../Common/PopupActions";
import { ButtonUI } from "../UI";
import StaffChatPopup from "./StaffChatPopup";

const RETRACT_STRIP_PX = 76;
const TRANSITION_MS = 260;
// ascunderea singură: mouse-ul iese din zona invizibilă cu HIDE_ZONE_PX mai mare decât popup-ul
// (pe fiecare latură) și rămâne afară LEAVE_DELAY_MS — o scăpare scurtă nu-l ascunde
const HIDE_ZONE_PX = 48;
const LEAVE_DELAY_MS = 300;
// pe telefon mesajele sunt o foaie de jos, ca popup-urile (scss/Popups/_staffChatPopup.scss): la
// închidere așteptăm să coboare; = $mobile din App.scss și durata din --popupUI-sheet-out
const SHEET_MEDIA = "(max-width: 767.98px)";
const SHEET_CLOSE_MS = 360;

function shouldAnimateSheetClose() {
   if (typeof window === "undefined" || !window.matchMedia) return false;
   return (
      window.matchMedia(SHEET_MEDIA).matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
   );
}

function useStaffChatWorkspace() {
   return useSyncExternalStore(
      subscribeStaffChatWorkspace,
      getStaffChatWorkspaceState,
      getStaffChatWorkspaceState,
   );
}

function isDesktopViewport() {
   return (
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 861px)").matches
   );
}

/**
 * Independent floating host for the Message workspace (left side).
 * Not part of PopupUI — no overlay. Pe calculator se ascunde singur la margine când mouse-ul iese
 * din zona puțin mai mare decât popup-ul (HIDE_ZONE_PX, după LEAVE_DELAY_MS), nu și cât scrii un
 * mesaj; reapare când mouse-ul trece pe banda rămasă sau la un clic pe ea. „Ascunde” îl ascunde
 * imediat. Alt popup deschis se așază după el (scss/Popups/_staffChatPopup.scss).
 */
export default function StaffChatWorkspaceHost() {
   const workspace = useStaffChatWorkspace();
   const panelRef = useRef(null);
   const leaveTimerRef = useRef(0);
   const [isDesktop, setIsDesktop] = useState(isDesktopViewport);
   const [otherPopupOpen, setOtherPopupOpen] = useState(
      () => Boolean(getCurrentPopup()),
   );

   useEffect(() => {
      if (typeof window === "undefined") return undefined;
      const mq = window.matchMedia("(min-width: 861px)");
      const onChange = () => setIsDesktop(mq.matches);
      onChange();
      if (mq.addEventListener) mq.addEventListener("change", onChange);
      else mq.addListener(onChange);
      return () => {
         if (mq.removeEventListener) mq.removeEventListener("change", onChange);
         else mq.removeListener(onChange);
      };
   }, []);

   const clearLeaveTimer = useCallback(() => {
      if (leaveTimerRef.current && typeof window !== "undefined") {
         window.clearTimeout(leaveTimerRef.current);
         leaveTimerRef.current = 0;
      }
   }, []);

   const requestExpand = useCallback(() => {
      if (!isDesktop) return;
      clearLeaveTimer();
      expandStaffChatWorkspace();
   }, [clearLeaveTimer, isDesktop]);

   const requestRetract = useCallback(() => {
      if (!isDesktop) return;
      clearLeaveTimer();
      retractStaffChatWorkspace();
   }, [clearLeaveTimer, isDesktop]);

   // alt popup deschis: mesajele rămân deschise (popup-ul stă după ele); doar stratul se schimbă
   useEffect(() => {
      return subscribePopup(() => {
         setOtherPopupOpen(Boolean(getCurrentPopup()));
      });
   }, []);

   // clic pe banda rămasă (ascunse) → reapar; un clic în afară nu le mai ascunde
   useEffect(() => {
      if (!workspace.isOpen || !isDesktop) return undefined;

      const onPointerDown = (event) => {
         const panel = panelRef.current;
         if (!(panel instanceof Element)) return;
         const target = event.target;
         if (!(target instanceof Node)) return;

         if (panel.contains(target) && workspace.dockMode === "retracted") requestExpand();
      };

      document.addEventListener("pointerdown", onPointerDown, true);
      return () => {
         document.removeEventListener("pointerdown", onPointerDown, true);
      };
   }, [workspace.isOpen, workspace.dockMode, isDesktop, requestExpand]);

   useEffect(() => () => clearLeaveTimer(), [clearLeaveTimer]);

   // ascunderea singură: urmărim mouse-ul cât popup-ul e deschis pe calculator; afară din zona
   // lărgită pornește ceasul, înăuntru îl oprește
   useEffect(() => {
      if (!workspace.isOpen || !isDesktop || workspace.dockMode !== "expanded") return undefined;

      const last = { x: 0, y: 0 };
      const insideZone = (x, y) => {
         const panel = panelRef.current;
         if (!(panel instanceof Element)) return true;
         const r = panel.getBoundingClientRect();
         return (
            x >= r.left - HIDE_ZONE_PX &&
            x <= r.right + HIDE_ZONE_PX &&
            y >= r.top - HIDE_ZONE_PX &&
            y <= r.bottom + HIDE_ZONE_PX
         );
      };
      // cât scrii (câmp de text din mesaje, cu ceva în el) nu-l ascundem
      const typingInside = () => {
         const panel = panelRef.current;
         const el = document.activeElement;
         return (
            panel instanceof Element &&
            el instanceof HTMLElement &&
            panel.contains(el) &&
            (el.tagName === "TEXTAREA" || el.tagName === "INPUT") &&
            String(el.value || "").trim() !== ""
         );
      };

      const onMove = (event) => {
         last.x = event.clientX;
         last.y = event.clientY;
         if (insideZone(last.x, last.y)) {
            clearLeaveTimer();
            return;
         }
         if (leaveTimerRef.current) return;
         leaveTimerRef.current = window.setTimeout(() => {
            leaveTimerRef.current = 0;
            if (insideZone(last.x, last.y) || typingInside()) return;
            retractStaffChatWorkspace();
         }, LEAVE_DELAY_MS);
      };

      document.addEventListener("pointermove", onMove, { passive: true });
      return () => {
         document.removeEventListener("pointermove", onMove);
         clearLeaveTimer();
      };
   }, [workspace.isOpen, workspace.dockMode, isDesktop, clearLeaveTimer]);

   // „Închide” și atingerea deasupra foii (telefon): foaia coboară, apoi mesajele se închid
   const [sheetLeaving, setSheetLeaving] = useState(false);
   const sheetTimerRef = useRef(0);
   const closeWorkspace = useCallback(() => {
      if (sheetTimerRef.current) return;
      if (!shouldAnimateSheetClose()) {
         closeStaffChatWorkspace();
         return;
      }
      setSheetLeaving(true);
      sheetTimerRef.current = window.setTimeout(() => {
         sheetTimerRef.current = 0;
         closeStaffChatWorkspace();
      }, SHEET_CLOSE_MS);
   }, []);

   useEffect(() => {
      if (workspace.isOpen) return;
      window.clearTimeout(sheetTimerRef.current);
      sheetTimerRef.current = 0;
      setSheetLeaving(false);
   }, [workspace.isOpen]);

   useEffect(() => () => window.clearTimeout(sheetTimerRef.current), []);

   // rândul de sus, ca la PopupUI: <PopupActions> din mesaje + „Ascunde” + „Închide”; lățimea lui
   // ajunge în --popupUI-bar-w, ca titlul și capul conversației să-și lase loc
   const [actionsHost, setActionsHost] = useState(null);
   const barRowRef = useRef(null);
   useEffect(() => {
      const row = barRowRef.current;
      const panel = panelRef.current;
      if (!row || !panel || typeof ResizeObserver === "undefined") return undefined;
      const sync = () => {
         panel.style.setProperty("--popupUI-bar-w", `${Math.ceil(row.getBoundingClientRect().width) + 24}px`);
      };
      sync();
      const ro = new ResizeObserver(sync);
      ro.observe(row);
      return () => ro.disconnect();
   }, [workspace.isOpen]);

   if (!workspace.isOpen) return null;

   const retracted = isDesktop && workspace.dockMode === "retracted";
   const style = {
      zIndex: otherPopupOpen ? 1030 : 1045,
      "--staff-chat-strip": `${RETRACT_STRIP_PX}px`,
      "--staff-chat-transition": `${TRANSITION_MS}ms`,
   };

   // voalul de deasupra foii, doar pe telefon (pe calculator mesajele n-au voal); cu alt popup
   // deschis peste mesaje se stinge, iar atingerea sus e a voalului acelui popup
   const overlay = (
      <div
         className={
            "staffChatWorkspace__overlay" +
            (otherPopupOpen || sheetLeaving ? "" : " is-open")
         }
         onPointerDown={(event) => {
            if (typeof event.button === "number" && event.button !== 0) return;
            closeWorkspace();
         }}
      />
   );

   const tree = (
      <div
         ref={panelRef}
         className={
            "staffChatWorkspace" +
            (retracted ? " staffChatWorkspace--retracted" : "") +
            (sheetLeaving ? " staffChatWorkspace--leaving" : "") +
            (workspace.view === "thread"
               ? " staffChatWorkspace--conversation"
               : "")
         }
         style={style}
         data-dock={workspace.dockMode}
         // ascunse la margine: mouse-ul pe banda rămasă le aduce înapoi
         onPointerEnter={retracted ? (event) => event.pointerType === "mouse" && requestExpand() : undefined}
         role="dialog"
         aria-label="Mesaje"
         aria-modal="false"
      >
         {/* același rând de sus ca la celelalte popup-uri */}
         <div className="popupPanelUI__bar staffChatWorkspace__bar">
            <div ref={barRowRef} className="popupPanelUI__barRow">
               <div ref={setActionsHost} className="popupPanelUI__actions" />
               {isDesktop && !retracted ? (
                  <ButtonUI
                     icon={hideIcon}
                     aria-label="Ascunde mesajele la margine"
                     title="Ascunde la margine (mouse-ul pe bandă le aduce înapoi)"
                     onClick={(event) => {
                        event.stopPropagation();
                        requestRetract();
                     }}
                  />
               ) : null}
               <ButtonUI
                  icon="close"
                  aria-label="Închide mesajele"
                  title="Închide"
                  className="popupPanelUI__close"
                  onClick={(event) => {
                     event.stopPropagation();
                     closeWorkspace();
                  }}
               />
            </div>
         </div>

         <div className="staffChatWorkspace__inner">
            <PopupActionsContext.Provider value={actionsHost}>
               <StaffChatPopup
                  conversationId={workspace.openProps?.conversationId}
                  student={workspace.openProps?.student}
                  openThread={Boolean(workspace.openProps?.openThread)}
               />
            </PopupActionsContext.Provider>
         </div>
      </div>
   );

   if (typeof document === "undefined") return tree;
   return createPortal(
      <>
         {overlay}
         {tree}
      </>,
      document.body,
   );
}
